/**
 * Deterministic Rolodex ids for FFS artifacts. Single source of truth for
 * generation, validation, and triage so the three paths can't drift on the
 * id scheme.
 */

const LOCAL_THIN_AREA_SET_ID_PREFIX = 'ffs-ltas'
const FFS_CONFIGURATION_ID_PREFIX = 'ffs-configuration'
const FFS_ASSET_MODEL_SNAPSHOT_ID_PREFIX = 'ffs-asset-model'

export const LOCAL_THIN_AREA_INDICATION_SET_SORT_ORDER = 1000

/**
 * Returns the deterministic Rolodex id for an asset ticket's LTA indication
 * set. Per-ticket scoping prevents two tickets covering the same component
 * from clobbering each other's LTAs. `unit` and `inspection` are NOT in
 * the id — they're written into `tags` on the same entry, and `(ticketId,
 * componentId)` is already unique within an asset workflow (one active
 * inspection per component per ticket, resolved at render time via
 * `pickMostRecentInspection` in `selectComponentInspection.ts` and threaded
 * through `ComponentScopeContext`). Including them in the id blew through
 * Rolodex's 75-char limit on real unit slugs.
 */
function getExpectedLtaSetId({
  ticketId,
  componentId,
}: {
  ticketId: string
  componentId: string
}): string {
  return `${LOCAL_THIN_AREA_SET_ID_PREFIX}_${ticketId}_${componentId}`
}

/**
 * Legacy (pre-ticket-scoped) LTA indication-set id. Used by:
 *   - `useActiveLtaSet` read fallback when no `ComponentScopeContext` is
 *     mounted (legacy linear FFS workflow).
 *   - `FfsBinningStep` write site when no `ComponentScopeContext` is
 *     mounted, so legacy tickets stay round-trippable.
 */
function getLegacyLtaSetId({
  unit,
  inspection,
  component,
}: {
  unit: string
  inspection: string
  component: string
}): string {
  return `${LOCAL_THIN_AREA_SET_ID_PREFIX}_${unit}_${inspection}_${component}`
}

/**
 * Deterministic Rolodex id for an asset ticket's FFS binning configuration.
 * Ticket-scoped so two tickets covering the same component don't share
 * inputs. Shared by `FfsBinningStep` (write site) and
 * `useFfsBinningValidation` (read site).
 */
function getExpectedFfsConfigId({
  ticketId,
  componentId,
}: {
  ticketId: string
  componentId: string
}): string {
  return `${FFS_CONFIGURATION_ID_PREFIX}_${ticketId}_${componentId}`
}

/**
 * Legacy (pre-ticket-scoped) FFS configuration id. Used by:
 *   - `useFfsBinningValidation` read fallback (legacy linear FFS workflow).
 *   - `FfsBinningStep` write site when no `ComponentScopeContext` is
 *     mounted, so legacy tickets stay round-trippable.
 */
function getLegacyFfsConfigId(componentId: string): string {
  return `${FFS_CONFIGURATION_ID_PREFIX}_${componentId}`
}

/**
 * The ONE place the scoped-vs-legacy id policy lives. Asset (tabbed)
 * tickets -- a `ComponentScopeContext` is mounted and the ticket id is
 * known -- persist under ticket-scoped ids so two tickets covering the
 * same component can't clobber each other. Legacy linear tickets keep the
 * historical unscoped shapes so read/write symmetry holds for pre-scope
 * data. Callers pass `component.id` (NOT a CDS/inspection tag) as the
 * component identity so every site derives it from the same source.
 */
export function resolveFfsArtifactIds({
  scoped,
  ticketId,
  componentId,
  unit,
  inspection,
}: {
  /** True when a `ComponentScopeContext` is mounted (asset/tabbed ticket). */
  scoped: boolean
  ticketId: string | undefined
  componentId: string
  /** Legacy LTA id inputs; only read on the legacy path. */
  unit: string
  inspection: string
}): { ffsConfigId: string; ltaSetId: string } {
  if (scoped && ticketId) {
    return {
      ffsConfigId: getExpectedFfsConfigId({ ticketId, componentId }),
      ltaSetId: getExpectedLtaSetId({ ticketId, componentId }),
    }
  }
  return {
    ffsConfigId: getLegacyFfsConfigId(componentId),
    ltaSetId: getLegacyLtaSetId({
      unit,
      inspection,
      component: componentId,
    }),
  }
}

/**
 * Deterministic Rolodex id for a ticket's asset-model snapshot -- the
 * per-ticket copy of the unit's `ASSET_MODEL` entry that asset-scoped FFS
 * workflows read (and edit) instead of the live model. Ticket-scoped and
 * unit-wide: one snapshot serves every component tab on the ticket, so
 * `componentId` is deliberately NOT part of the id. The source unit is not
 * in the id either (and, unlike the LTA/config entries above, not in `tags`
 * -- omitting the `unit` tag is what keeps snapshots invisible to every
 * unit-scoped ASSET_MODEL query). Shared by `useFfsAssetModelSnapshot`
 * (read + lazy-create site) and, via the model's own `id`, the
 * `EditGeckoRichAssetModel` save path.
 */
export function getExpectedFfsAssetModelSnapshotId({
  ticketId,
}: {
  ticketId: string
}): string {
  return `${FFS_ASSET_MODEL_SNAPSHOT_ID_PREFIX}_${ticketId}`
}
